<template>
  <div v-loading="plotLoading">
    <div v-if="distribution === 'daily'" class="flex align-center">
      <h2 class="mt-0 mb-0">
        <v-svg
          v-if="direction === 'IN'"
          sprite="call-inbound"
          class="voxo-pink"
        />
        <v-svg v-else sprite="call-outbound" class="voxo-pink"></v-svg>
        {{
          direction === "IN" || direction === 'queue'
            ? "Inbound"
            : "Outbound"
        }}
        Heatmap
      </h2>
      <el-radio-group v-model="calendarHeatmap.duration" class="ml-10">
        <el-radio-button type="default" label="6"
          >Last 6 Months</el-radio-button
        >
        <el-radio-button label="12">Last 1 Year</el-radio-button>
      </el-radio-group>
    </div>

    <div
      v-show="plotRequested"
      :id="`heatmap-plot-${direction}`"
      class="heatmap-canvas"
      style="height: 350px; width:100%;"
    ></div>

    <div v-if="!plotRequested" class="mt-20">
      <el-tag class="mb-10">
        <i class="el-icon-warning" style="margin-right: 4px"></i>
        Select a date range above to get started
      </el-tag>
    </div>

  </div>
</template>

<script>
import echarts from "echarts";
import { debounce, isEqual } from "lodash";
import moment from "moment";
import feathersClient from "@/feathers/feathers-client";
export default {
  name: "HeatmapPlot",
  props: {
    direction: {
      required: true
    },
    metaData: {
      required: true
    }
  },
  data() {
    return {
      chart: null,
      chartEl: null,
      plotData: [],
      distribution: "daily",
      plotRequested: false,
      // ⭐️ CALENDAR HEATMAP ⭐️ //
      calendarHeatmap: {
        data: null,
        duration: ""
      },
      endDate: moment().format("YYYY-MM-DD"),
      startDate: moment()
        .subtract(6, "months")
        .format("YYYY-MM-DD"),
      plotLoading: false
    };
  },
  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    labelColor() {
      return this.$store.state.layout.theme === "theme-dark"
        ? "rgba(255,255,255,0.5)"
        : "rgba(0,0,0, 0.5)";
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    calendarDuration() {
      return this.calendarHeatmap.duration;
    },
    queueId() {
      return this.metaData.queueId;
    }
  },
  watch: {
    calendarDuration(duration) {
      if(duration){
        this.plotLoading = true;
        const buildCalendarChart = debounce(() => {
          this.startDate = moment()
            .subtract(parseInt(duration), "months")
            .format("YYYY-MM-DD");
          this.queryCalendarHeatmapData();
        }, 500);
        buildCalendarChart();
      }
    },
    selectedTenant() {
      this.plotRequested = false;
      this.calendarHeatmap.duration = "";
    },
    queueId() {
      this.plotRequested = false;
      this.calendarHeatmap.duration = "";
    }
  },
  methods: {
    buildCalendarHeatmap() {
      if (this.chart) {
        this.chart.dispose();
      }
      if (!this.chartEl) {
        return;
      }
      this.chart = echarts.init(this.chartEl);
      this.buildCalendarHeatmapData(this.plotData);
      const plotData = this.calendarHeatmap.data.map(d => d[0][1]);
      const visualMapMin = plotData.length ? Math.min(...plotData) : 0;
      const visualMapMax = plotData.length ? Math.max(...plotData) : 100;
      this.chart.setOption({
        tooltip: {},
        visualMap: {
          min: visualMapMin,
          max: visualMapMax,
          type: "piecewise",
          orient: "horizontal",
          show: visualMapMin !== visualMapMax,
          left: "center",
          textStyle: {
            color: this.labelColor
          },
          top: 0,
          formatter: (val1, val2) => {
            return `${Math.ceil(val1)} - ${Math.ceil(val2)}`;
          }
        },
        calendar: {
          top: 55,
          left: 30,
          right: 10,
          cellSize: ["auto", 35],
          dayLabel: {
            color: this.labelColor
          },
          monthLabel: {
            color: this.labelColor
          },
          yearLabel: {
            show: false,
            color: this.labelColor
          },
          range: [this.startDate, this.endDate],
          itemStyle: {
            borderWidth: 0.5
          }
        },
        series: {
          type: "heatmap",
          coordinateSystem: "calendar",
          data: this.calendarHeatmap.data.map(d => d[0]),
          tooltip: {
            formatter: param => {
              const data = param.data;
              const index = this.calendarHeatmap.data.findIndex(d =>
                isEqual(d[0], data)
              );
              const labels = this.calendarHeatmap.data[index][1];

              return [
                `<span style="font-weight: bold;">${param.value[0]}</span><br />`,
                ...labels
              ].join("");
            }
          }
        }
      });
    },
    buildCalendarHeatmapData(data) {
      const chartData = [];
      for (const key of Object.keys(data)) {
        if (data[key]) {
          const x = data[key];
          let sum = 0;

          if (this.direction === "queue") {
            sum = x.totalInbound;
            const labelMap = {
              totalInbound: "Total Inbound"
            };
            const indexMap = {
              totalInbound: 0,
            };

            const labels = [
              "Total Inbound: 0 <br />"
            ];

            for (const key in x) {
              if (labelMap[key]) {
                labels[indexMap[key]] = `${labelMap[key]}: ${x[key]}<br />`;
              }
            }

            chartData.push([[key, sum], labels]);
          } else {
            sum = x
              .map(d => d.total)
              .reduce((newVal, prevVal) => newVal + prevVal, 0);

            const labelMap = {
              ANSWERED: "Answered",
              "NO ANSWER": "No Answer",
              BUSY: "Busy",
              FAILED: "Failed"
            };
            const indexMap = {
              ANSWERED: 0,
              "NO ANSWER": 1,
              BUSY: 2,
              FAILED: 3
            };

            const labels = [
              `Answered (${this.direction}): 0 <br />`,
              `No Answer (${this.direction}): 0 <br />`,
              `Busy (${this.direction}): 0 <br />`,
              `Failed (${this.direction}): 0 <br />`
            ];

            for (const { disposition, total } of x) {
              if (labelMap[disposition]) {
                labels[
                  indexMap[disposition]
                ] = `${labelMap[disposition]} (${this.direction}): ${total}<br />`;
              }
            }

            chartData.push([[key, sum], labels]);
          }
        }
      }
      this.calendarHeatmap.data = chartData;
    },

    handleQuery({ service, query, direction }) {
      if (direction === "queue") {
        return feathersClient.service(service).create(query);
      }

      return feathersClient.service(service).find({ query });
    },

    queryCalendarHeatmapData() {
      this.plotLoading = true;
      this.plotRequested = true;
      const {
        service,
        queryType,
        tenantId,
        dispositions,
        extNumber,
        tenantCode,
        queueId,
        primaryFilter,
      } = this.metaData;

      let query;

      //return if for queue and there is no queueID specified!
      if(this.direction === "queue" && !this.queueId) return;

      if (this.direction === "queue" && this.queueId) {
        query = {
          type: queryType,
          tenantId,
          tenantCode,
          queueId,
          dateStart: this.startDate,
          dateEnd: this.endDate,
          dispositions,
          extNumber,
          timezone: this.user.timezone,
          distribution: this.distribution
        };
      } else {
        query = {
          queryType,
          tenantId,
          dateStart: this.startDate,
          dateEnd: this.endDate,
          direction: this.direction,
          dispositions,
          extNumber,
          primaryFilter: primaryFilter || "",
          timezone: this.user.timezone,
          distribution: this.distribution
        };
      }

      this.handleQuery({ service, query, direction: this.direction }).then(res => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const data = this.direction === "queue"
          ? res.primary.data.distributionData
          : res.primary.data;

        // Transform keys to include the year in "YYYY/MM/DD" format
        const transformedData = {};
        Object.keys(data).forEach(date => {
          const [month, day] = date.split("/").map(Number);
          const year = month <= currentMonth ? currentYear : currentYear - 1; // Determine the year
          const formattedDate = `${year}/${String(month).padStart(2, "0")}/${String(day).padStart(2, "0")}`;
          transformedData[formattedDate] = data[date];
        });

        this.plotData = transformedData;
        this.buildCalendarHeatmap();
        this.plotLoading = false;
      });
    }
  },
  mounted() {
    this.chartEl = document.getElementById(`heatmap-plot-${this.direction}`);
    // this.queryCalendarHeatmapData();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
      this.chart = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.heatmap-plot {
  margin-top: 10px;
  width: 100%;
}
.heatmap-canvas {
  width: auto;
  padding: 0 10px;
  max-width: 1250px;
  margin: 30px auto 0 auto;
}
</style>
